import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, NumberInput, FormInstance, SelectPicker } from 'rsuite';
import PageContent from '@/components/PageContent';
import { BaseErrorModel, BaseResponseModel, LatLng, PageState } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { RunEventDistance, RunEventDistanceTypeEnum, RunEventDistanceTypeList, UpsertRunEventDistance } from '@/models/run-event-distance.model';
import { upsertRunEventDistance } from '@/services/run-event-distance.service';
import ImageUploader from '@/components/ImageUploader';
import { MdMilitaryTech } from 'react-icons/md';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import { getRunEvent, getRunEventsLookup } from '@/services/run-event.service';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { RunEvent } from '@/models/run-event.model';
import Textarea from '@/components/Textarea';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { hk_lat_lng } from '@/constants/map';
import { getBoundingBoxCenter, getPolylinePathFromGeoJson } from '@/utils/map';

const RunEventDistanceSchema = Schema.Model<UpsertRunEventDistance>({
  id: Schema.Types.NumberType(),
  startLat: Schema.Types.NumberType().isRequired('This field is required.'),
  startLon: Schema.Types.NumberType().isRequired('This field is required.'),
  endLat: Schema.Types.NumberType().isRequired('This field is required.'),
  endLon: Schema.Types.NumberType().isRequired('This field is required.'),
  runEventId: Schema.Types.NumberType().isRequired('This field is required.'),
  startDate: Schema.Types.DateType().isRequired('This field is required.'),
  type: Schema.Types.NumberType().isRequired('This field is required.'),
  medalImg: Schema.Types.StringType(),
  jsonRoute: Schema.Types.StringType(),
  distance: Schema.Types.NumberType().when(schemaSpec => {
    const type = schemaSpec.type.value as RunEventDistanceTypeEnum | undefined;
    if (type != undefined && type == RunEventDistanceTypeEnum.Other) {
      return Schema.Types.NumberType().isRequired('This field is required when Record Type is Other.');
    }
    return Schema.Types.NumberType();
  }),
});

const emptyModel: UpsertRunEventDistance = {
  id: undefined,
  startLat: undefined,
  endLat: undefined,
  startLon: undefined,
  endLon: undefined,
  runEventId: undefined,
  startDate: undefined,
  distance: undefined,
  medalImg: undefined,
  jsonRoute: undefined
};

interface RunEventDistanceFormProps{
  runEventId?: number,
  formObject?: RunEventDistance;
  pageState: PageState,
};

const RunEventDistanceForm: React.FC<RunEventDistanceFormProps> = ({runEventId, formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertRunEventDistance>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedRunEvent, setSelectedRunEvent] = useState< RunEvent | undefined>(undefined);

  const [polylinePath, setPolylinePath] = useState<LatLng[]>([]);
  const [mapCenter, setMapCenter] = useState<LatLng|undefined>(hk_lat_lng);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    
    if(pageState == PageState.New){
      setFormValue({...emptyModel, runEventId});
      updateSelectedRunEvent(runEventId);
      return;
    }
    
    if (formObject) {  
      setFormValue(formObject);
      updateSelectedRunEvent(formObject.runEventId);
      updatePolyline(formObject?.jsonRoute ?? '');
    } else {
      setFormValue(emptyModel);
    }
    
  }, [formObject]);

  useEffect(() => {
    updateMapCenter();
  }, [formValue, selectedRunEvent, polylinePath]);

  useEffect(() => {
    updatePolyline(formValue.jsonRoute ?? '');
  }, [formValue.jsonRoute]);
  
  const updateMapCenter = () => {
    if(polylinePath && polylinePath.length > 0){
      setMapCenter(getBoundingBoxCenter(polylinePath));
      return;
    }
    if((formValue?.startLat && formValue?.startLon)){
      setMapCenter({lat: formValue.startLat, lng: formValue.startLon});
      return;
    }
    if((formValue?.endLat && formValue?.endLon)){
      setMapCenter({lat: formValue.endLat, lng: formValue.endLon});
      return;
    }
    if(selectedRunEvent){
      setMapCenter({lat: selectedRunEvent.city.lat, lng: selectedRunEvent.city.lon});
      return;
    }
    setMapCenter(hk_lat_lng);
  };

  const updateSelectedRunEvent = async(runEventId: number|undefined) => {
    if(!runEventId){
      setSelectedRunEvent(undefined);
      return;
    }

    const runEvent = (await getRunEvent(runEventId)).result;
    setSelectedRunEvent(runEvent);
  };

  const handleSubmit = async () => {
    // console.log(formValue);
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const casted = {...formValue, jsonRoute: hasJsonRoute() ? formValue.jsonRoute : undefined};
      const result: BaseResponseModel<RunEventDistance|null> = await upsertRunEventDistance(casted);
      if (result.success) {
        nav(`/run-events/${formValue.runEventId}`, {state:{tab: "run-event-distances"}});
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const hasJsonRoute = () => {
    return !!(formValue.jsonRoute ?? '').trim();
  };


  const handleRunEventChange = (value) => {
    setFormValue(pre => ({ ...pre, runEventId: value}));
    updateSelectedRunEvent(value);
  };

  const updatePolyline = (geoJson: string) : LatLng[]=> {
    const polyline = getPolylinePathFromGeoJson(geoJson);
    if(polyline && polyline.length > 0) {
      setPolylinePath(polyline);
      return polyline;
    } else { 
      setPolylinePath([]);
      return [];
    }
  };

  const handleJsonChange = (geoJson: string) => {

    const polyline = updatePolyline(geoJson);

    if(polyline.length > 0){
      setFormValue(prevFormValue => ({
        ...prevFormValue,
        startLat: polyline[0].lat,
        startLon: polyline[0].lng,
        endLat: polyline[polyline.length - 1].lat,
        endLon: polyline[polyline.length - 1].lng,
      }));
    }
  };
  const handleOnMapClick = (e: google.maps.MapMouseEvent) => {
    if (hasJsonRoute()) return;
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setFormValue(pre => {
        const startMissing = pre.startLat == null || pre.startLon == null;
        const endMissing = pre.endLat == null || pre.endLon == null;
        if (!startMissing && !endMissing) return pre;
        const next = {
          ...pre,
          startLat: startMissing ? position.lat : pre.startLat,
          startLon: startMissing ? position.lng : pre.startLon,
          endLat: (!startMissing && endMissing) ? position.lat : pre.endLat,
          endLon: (!startMissing && endMissing) ? position.lng : pre.endLon,
        };
        return next;
      });
    }
  };

  const handleMarkerDrag = (key: string, e: google.maps.MapMouseEvent) => {
    if (hasJsonRoute()) return;
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };

      setFormValue(pre => {
        const next = key === 'start'
          ? { ...pre, startLat: position.lat, startLon: position.lng }
          : { ...pre, endLat: position.lat, endLon: position.lng };
        return next;
      });
    }
  };

  const polylineOptions = {
    strokeColor: '#FF6F61', // coral-red color
    strokeOpacity: 0.8,
    strokeWeight: 5,
    geodesic: true,
  };  
  return (
    <>
      <PageContent>
        <Form fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={RunEventDistanceSchema}
          disabled={loading}
        >
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:8}} >
                <Row>
                  <Col span={{xs:24}}>
                    <Form.Group>
                      <Form.Label>Medal Image</Form.Label>
                      <Form.Control
                        accepter={ImageUploader}
                        name="medalImg"
                        defaultIcon={MdMilitaryTech}
                        height="150px"
                        width="150px"
                        url={formValue.medalImg}
                        objectStorageCategory={ObjectStorageFileCategory.RunEventDistance}
                        onChange={url => setFormValue(pre => ({...pre, flagImageUrl: url}))}              
                        />
                    </Form.Group>               
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24}}>
                    <Form.Group>
                      <Form.Label>RunEvent</Form.Label>
                      <Form.Control name="runEventId" accepter={FetchingSelect} block
                        fetchData={(text, page) => getRunEventsLookup(text, page)} 
                        placeholder="Select a run event"
                        disabled
                        onChange={handleRunEventChange}
                      /> 
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24}}>
                    <Form.Group>
                      <Form.Label>Start Date</Form.Label>
                        <Form.Control accepter={ZonedDatePicker} block
                          name="startDate" 
                          value={formValue.startDate ?? null}
                          onChange={dt => setFormValue(pre => ({...pre, startDate: dt ?? undefined}))}
                          zoneIanaId={selectedRunEvent?.city?.ianaTimeZoneId}
                        />                  
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Control name="type" accepter={SelectPicker} block
                        data={RunEventDistanceTypeList} 
                        searchable={false}
                        placeholder="Select a type"
                      /> 
                    </Form.Group>                    
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Distance</Form.Label>
                      <Form.Control name="distance" accepter={NumberInput} /> 
                    </Form.Group>                  
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Start Latitude</Form.Label>
                      <Form.Control name="startLat" accepter={NumberInput}
                        value={formValue.startLat ?? null}
                        disabled={hasJsonRoute()} />
                    </Form.Group>
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Start Longitude</Form.Label>
                      <Form.Control name="startLon" accepter={NumberInput}
                        value={formValue.startLon ?? null}
                        disabled={hasJsonRoute()} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>End Latitude</Form.Label>
                      <Form.Control name="endLat" accepter={NumberInput}
                        value={formValue.endLat ?? null}
                        disabled={hasJsonRoute()} />
                    </Form.Group>                  
                  </Col>
                  <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>End Longitude</Form.Label>
                      <Form.Control name="endLon" accepter={NumberInput}
                        value={formValue.endLon ?? null}
                        disabled={hasJsonRoute()} />
                    </Form.Group>                 
                  </Col>
                </Row>
                <Row>
                  
                  <Col span={{xs:24}}>
                    <Form.Group>
                      <Form.Label>Json Route Path</Form.Label>
                      <Form.Control 
                        name="jsonRoute" 
                        value={formValue.jsonRoute ?? ''} 
                        accepter={Textarea} 
                        rows = {10}
                        onChange={handleJsonChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>        
              </Col>
              
              <Col span={{xs:24, xl:16}} style={{textAlign: 'center'}}>

                <Form.Group>
                  <Form.Label>{
                    (!formValue.startLat || !formValue.startLon) ? 
                    'Click on map to select START point' : 
                    (!formValue.endLat || !formValue.endLon) ? 
                    'Click on map to select END point' : 
                    'Drag START or END markers'}
                  </Form.Label>
                    {isLoaded && <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '750px', borderRadius: 10 }}
                      center={mapCenter}
                      zoom={14}
                      onClick={handleOnMapClick}
                    >
                      {polylinePath && polylinePath.length > 0 && <Polyline path={polylinePath} options={polylineOptions}/>}
                      {formValue.startLat != null && formValue.startLon != null && <Marker
                        key={'start'} 
                        label={'S'} 
                        position={{lat:formValue.startLat, lng:formValue.startLon}} 
                        onDragEnd={e => handleMarkerDrag('start', e)}
                        draggable={!hasJsonRoute()}
                      />}
                      {formValue.endLat != null && formValue.endLon != null && <Marker
                        key={'end'} 
                        label={'E'} 
                        position={{lat:formValue.endLat, lng:formValue.endLon}} 
                        onDragEnd={e => handleMarkerDrag('end', e)}
                        draggable={!hasJsonRoute()}
                      />}
                    </GoogleMap>}
                </Form.Group>                
              </Col>
            </Row>
          </Grid>
        </Form>

        <Divider />
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

        <Stack justifyContent="space-between">
          <IconButton icon={<PagePreviousIcon />} onClick={() => nav(`/run-events/${formValue.runEventId}`, {state:{tab: "run-event-distances"}})}> 
            Back
          </IconButton>
          <IconButton
            icon={<PageNextIcon />}
            placement="right"
            appearance="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            Save
          </IconButton>
        </Stack>
      </PageContent>
    </>
  );
};

export default RunEventDistanceForm;
